import { type ZodType } from 'zod'
/**
 * A composable for sending Google Analytics 4 (GA4) events with validation and optional configurations.
 *
 * @template TEvent - Generic type representing the event structure
 * @param {ZodType<TEvent>} schema - Zod schema for validating the event structure
 * @param {TEvent} event - The event object to be sent to Google Tag Manager
 * @param {Object} [options] - Optional configuration for event sending
 * @param {boolean} [options.clearEcommerce=false] - Whether to clear previous ecommerce data before sending
 * @param {boolean} [options.debug=false] - Enable debug logging of events
 * @param {boolean} [options.waitForDatalayerSetup=false] - Wait for datalayer setup before sending event
 * @returns {void}
 * @throws {Error} Logs an error if event validation fails
 * @example
 * // Send a simple page view event
 * useGASendEvent(pageViewSchema, {
 *   event: 'page_view',
 *   page_location: window.location.href
 * });
 *
 * // Send an event with debug mode and ecommerce clearing
 * useGASendEvent(purchaseSchema, purchaseEvent, {
 *   clearEcommerce: true,
 *   debug: true
 * });
 */
export const useGASendEvent = <TEvent extends Record<string, any>>(
  schema: ZodType<TEvent>,
  event: TEvent,
  options?: {
    clearEcommerce?: boolean
    debug?: boolean
    waitForDatalayerSetup?: boolean // wait for datalayer_setup event
  }
) => {
  // Check if GTM-ID is present
  if (!useRuntimeConfig().public.scripts.googleTagManager.id) return
  if (!import.meta.client) return

  const { dataLayer } = useScriptGoogleTagManager()
  const isValid = schema.safeParse(event).success

  if (!isValid) {
    console.error(
      `Nuxt GA4 - Wrong validation: ${event?.event ?? ''}` +
        schema.safeParse(event).error
    )
    return
  }

  if (options?.debug) {
    console.log('Nuxt GA4 - debug event', {
      ...event,
    })
  }

  const sendEvent = () => {
    if (options?.clearEcommerce) dataLayer.push({ ecommerce: null })
    dataLayer.push(event)
  }

  const nuxt = useNuxtApp()
  if (options?.waitForDatalayerSetup) {
    //@ts-ignore
    const stopHook = nuxt.hook('app:after-page-view', () => {
      sendEvent()
      stopHook()
    })
  } else {
    sendEvent()
  }
}
